import { useEffect, useMemo, useState } from "react";

const apiUrl =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

function getStoredAuthToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken")
  );
}

export function getCatalogEntryId(entry) {
  return entry?._id || entry?.id;
}

export default function useCatalogEntryPersonalization(
  catalogHome
) {
  const [favoriteEntries, setFavoriteEntries] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [pinnedIds, setPinnedIds] = useState(new Set());

  useEffect(() => {
    const backendFavoriteEntries =
      catalogHome?.favoriteEntries || [];

    setFavoriteEntries(backendFavoriteEntries);

    setFavoriteIds(
      new Set(
        backendFavoriteEntries
          .map((entry) => getCatalogEntryId(entry))
          .filter(Boolean)
          .map((id) => id.toString())
      )
    );
  }, [catalogHome?.favoriteEntries]);

  useEffect(() => {
    const backendPinnedEntries =
      catalogHome?.pinnedEntries || [];

    setPinnedIds(
      new Set(
        backendPinnedEntries
          .map((entry) => getCatalogEntryId(entry))
          .filter(Boolean)
          .map((id) => id.toString())
      )
    );
  }, [catalogHome?.pinnedEntries]);

  const decorateEntryWithViewerContext = useMemo(
    () => (entry) => {
      const entryId = getCatalogEntryId(entry);
      const entryIdString =
        entryId ? entryId.toString() : null;

      return {
        ...entry,
        viewerContext: {
          ...(entry.viewerContext || {}),
          isFavorite:
            entryIdString
              ? favoriteIds.has(entryIdString)
              : false,
          isPinned:
            entryIdString
              ? pinnedIds.has(entryIdString)
              : false,
        },
      };
    },
    [favoriteIds, pinnedIds]
  );

  const decorateEntriesWithViewerContext = useMemo(
    () => (entries = []) =>
      entries.map(decorateEntryWithViewerContext),
    [decorateEntryWithViewerContext]
  );

  const handleToggleFavorite = async (entry) => {
    const entryId = getCatalogEntryId(entry);

    if (!entryId) {
      return;
    }

    const entryIdString = entryId.toString();
    const wasFavorite = favoriteIds.has(entryIdString);

    setFavoriteIds((previousIds) => {
      const nextIds = new Set(previousIds);

      if (wasFavorite) {
        nextIds.delete(entryIdString);
      } else {
        nextIds.add(entryIdString);
      }

      return nextIds;
    });

    setFavoriteEntries((previousEntries) => {
      if (wasFavorite) {
        return previousEntries.filter(
          (favoriteEntry) =>
            getCatalogEntryId(favoriteEntry)?.toString() !==
            entryIdString
        );
      }

      return [
        decorateEntryWithViewerContext(entry),
        ...previousEntries,
      ];
    });

    try {
      const token = getStoredAuthToken();

      const response = await fetch(
        `${apiUrl}/catalog/${entryIdString}/favorite`,
        {
          method: "POST",
          headers: {
            ...(token
              ? { Authorization: `Bearer ${token}` }
              : {}),
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            data.error ||
            "Could not update saved item."
        );
      }
    } catch (err) {
      setFavoriteIds((previousIds) => {
        const nextIds = new Set(previousIds);

        if (wasFavorite) {
          nextIds.add(entryIdString);
        } else {
          nextIds.delete(entryIdString);
        }

        return nextIds;
      });

      setFavoriteEntries((previousEntries) => {
        if (wasFavorite) {
          return [
            decorateEntryWithViewerContext(entry),
            ...previousEntries,
          ];
        }

        return previousEntries.filter(
          (favoriteEntry) =>
            getCatalogEntryId(favoriteEntry)?.toString() !==
            entryIdString
        );
      });

      console.error(err);
    }
  };

  const handleTogglePinned = async (entry) => {
    const entryId = getCatalogEntryId(entry);

    if (!entryId) {
      return;
    }

    const entryIdString = entryId.toString();
    const wasPinned = pinnedIds.has(entryIdString);

    setPinnedIds((previousIds) => {
      const nextIds = new Set(previousIds);

      if (wasPinned) {
        nextIds.delete(entryIdString);
      } else {
        nextIds.add(entryIdString);
      }

      return nextIds;
    });

    try {
      const token = getStoredAuthToken();

      const response = await fetch(
        `${apiUrl}/catalog/${entryIdString}/pin`,
        {
          method: "POST",
          headers: {
            ...(token
              ? { Authorization: `Bearer ${token}` }
              : {}),
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            data.error ||
            "Could not update pinned item."
        );
      }
    } catch (err) {
      setPinnedIds((previousIds) => {
        const nextIds = new Set(previousIds);

        if (wasPinned) {
          nextIds.add(entryIdString);
        } else {
          nextIds.delete(entryIdString);
        }

        return nextIds;
      });

      console.error(err);
    }
  };

  return {
    favoriteEntries,
    favoriteIds,
    pinnedIds,
    decorateEntryWithViewerContext,
    decorateEntriesWithViewerContext,
    handleToggleFavorite,
    handleTogglePinned,
  };
}
